import React, { useState } from 'react';
import { AlertTriangle, ShieldAlert, HeartPulse, Stethoscope, UserCog, MapPin } from 'lucide-react';

function cx(...classes) {
    return classes.filter(Boolean).join(' ');
}

// Members send a location heartbeat every 60s (see mobile app AppContext.tsx) — a gap
// much beyond that means their phone has lost signal, been backgrounded past the OS's
// limit, or they've gone off duty, so a team leader should be able to tell the
// difference between "live" and "last known" at a glance rather than trusting every
// pin on the map equally.
function describeFreshness(lastSeenAt) {
    if (!lastSeenAt) return { text: 'No location reported yet', dotCls: 'bg-neutral-400' };
    const seconds = Math.floor((Date.now() - new Date(lastSeenAt).getTime()) / 1000);
    if (seconds < 0) return { text: 'Just now', dotCls: 'bg-green-500' };
    if (seconds < 120) return { text: `Updated ${seconds}s ago`, dotCls: 'bg-green-500' };
    const minutes = Math.floor(seconds / 60);
    if (minutes < 10) return { text: `Updated ${minutes}m ago`, dotCls: 'bg-orange-500' };
    if (minutes < 60) return { text: `Stale — ${minutes}m ago`, dotCls: 'bg-red-500' };
    const hours = Math.floor(minutes / 60);
    return { text: `Stale — ${hours}h ago`, dotCls: 'bg-red-500' };
}

// Maps a member's role (or the default "incident" marker) to an icon + color,
// mirroring the mockup's .smart-marker role variants.
const ROLE_CONFIG = {
    incident: { icon: AlertTriangle, className: 'bg-red-700' },
    rsos: { icon: ShieldAlert, className: 'bg-seha-500' },
    nurses: { icon: HeartPulse, className: 'bg-orange-500' },
    doctors: { icon: Stethoscope, className: 'bg-green-600' },
    admins: { icon: UserCog, className: 'bg-purple-600' },
};

const IncidentMarker = ({ name = 'Sadia', distance = null, team = 'brown', showDetails = false, role = 'incident', lastSeenAt }) => {
    const [open, setOpen] = useState(false);

    const config = ROLE_CONFIG[role] || { icon: MapPin, className: 'bg-neutral-500' };
    const Icon = config.icon;
    const freshness = describeFreshness(lastSeenAt);

    const handleEnter = () => {
        if (showDetails) setOpen(true);
    };
    const handleLeave = () => setOpen(false);

    return (
        <div className="relative">
            <button
                type="button"
                onMouseOver={handleEnter}
                onMouseLeave={handleLeave}
                onFocus={handleEnter}
                onBlur={handleLeave}
                className={cx('smart-marker', config.className)}
                aria-label={name}
            >
                <Icon className="w-[18px] h-[18px]" />
                {showDetails && (
                    <span
                        className={cx('absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full ring-2 ring-white', freshness.dotCls)}
                        title={freshness.text}
                    />
                )}
            </button>

            {open && showDetails && (
                <div className="absolute z-30 bottom-full left-1/2 -translate-x-1/2 mb-3 w-56 bg-white rounded-xl border border-neutral-200 shadow-panel p-3.5 text-xs pointer-events-none dark:bg-ink-900 dark:border-neutral-700">
                    <p className="text-sm font-bold text-neutral-900 truncate dark:text-neutral-50">{name}</p>
                    <div className="mt-2 space-y-1 text-neutral-500 dark:text-neutral-500">
                        <p><span className="font-semibold text-neutral-700 dark:text-neutral-300">Role:</span> {role}</p>
                        <p><span className="font-semibold text-neutral-700 dark:text-neutral-300">Team:</span> {team}</p>
                        <p><span className="font-semibold text-neutral-700 dark:text-neutral-300">Distance:</span> {distance != null ? `${distance}km` : 'Unknown'}</p>
                        <p className="flex items-center gap-1.5 pt-1">
                            <span className={cx('w-1.5 h-1.5 rounded-full', freshness.dotCls)} />
                            {freshness.text}
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
};

export default IncidentMarker;
